import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../App';

// ── Constants ─────────────────────────────────────────────────────────────────

const MEETING_TYPE_STYLES = {
  'Board Meeting':      { bg: 'rgba(241,255,92,0.2)',  color: '#8a9100' },
  'Management Meeting': { bg: 'rgba(98,240,255,0.2)',  color: '#007a8a' },
  'Other':              { bg: 'rgba(189,255,192,0.3)', color: '#1a6b1a' },
};

const DOT_COLORS = {
  'Board Meeting':      '#F1FF5C',
  'Management Meeting': '#62F0FF',
  'Other':              '#8BFF9F',
  'holiday':            '#FF766B',
};

const DAY_HEADERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const LEGEND_ITEMS = [
  { label: 'Board',      color: DOT_COLORS['Board Meeting'] },
  { label: 'Management', color: DOT_COLORS['Management Meeting'] },
  { label: 'Other',      color: DOT_COLORS['Other'] },
  { label: 'Holiday',    color: DOT_COLORS['holiday'] },
];

// ── Pure utility functions ────────────────────────────────────────────────────

function toDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseDateLocal(dateStr) {
  if (!dateStr || dateStr === 'N/A') return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function formatDate(dateStr) {
  const date = parseDateLocal(dateStr);
  if (!date) return 'N/A';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function formatFull(date) {
  return date.toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
}

// ── Calendar ──────────────────────────────────────────────────────────────────

function Calendar({ meetingDates }) {
  const todayRef  = useRef(new Date());
  const today     = todayRef.current;
  const todayKey  = useMemo(() => toDateKey(today), [today]);

  const [viewYear,    setViewYear]    = useState(today.getFullYear());
  const [viewMonth,   setViewMonth]   = useState(today.getMonth());
  const [holidays,    setHolidays]    = useState({});
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const fetchHolidays = async () => {
      try {
        const res  = await fetch(`${API_BASE}/api/holidays/${viewYear}`, { signal: controller.signal });
        const data = await res.json();
        const map  = {};
        data.forEach(h => { if (h.date) map[h.date] = h.localName || h.name; });
        setHolidays(map);
      } catch (err) {
        if (err.name !== 'AbortError') setHolidays({});
      }
    };
    fetchHolidays();
    return () => controller.abort();
  }, [viewYear]);

  const prevMonth = useCallback(() => {
    setViewMonth(m => { if (m === 0) { setViewYear(y => y - 1); return 11; } return m - 1; });
    setSelectedDay(null);
  }, []);

  const nextMonth = useCallback(() => {
    setViewMonth(m => { if (m === 11) { setViewYear(y => y + 1); return 0; } return m + 1; });
    setSelectedDay(null);
  }, []);

  const goToToday = useCallback(() => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
    setSelectedDay(null);
  }, [today]);

  const { cells, daysInMonth } = useMemo(() => {
    const firstDay    = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells       = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return { cells, daysInMonth };
  }, [viewYear, viewMonth]);

  // meetingDates merges document dates AND scheduled meeting dates
  const meetingMap = useMemo(() => ({ ...meetingDates }), [meetingDates]);

  const getDayInfo = useCallback((day) => {
    const date      = new Date(viewYear, viewMonth, day);
    const key       = toDateKey(date);
    const isToday   = key === todayKey;
    const dow       = date.getDay();
    const isWeekend = dow === 0 || dow === 6;
    const holiday   = holidays[key] || null;
    const meetings  = meetingMap[key] || [];
    return { key, isToday, isWeekend, holiday, meetings };
  }, [viewYear, viewMonth, todayKey, holidays, meetingMap]);

  const monthName = useMemo(() =>
    new Date(viewYear, viewMonth).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
  [viewYear, viewMonth]);

  const selectedInfo = useMemo(() =>
    selectedDay ? getDayInfo(selectedDay) : null,
  [selectedDay, getDayInfo]);

  return (
    <div style={{
      background: '#fff', border: '0.5px solid #AAB0B8', borderRadius: '12px',
      padding: '20px', height: '100%', boxSizing: 'border-box',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: '15px', fontWeight: '600', color: '#22272D', margin: 0 }}>
          Calendar
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button type="button" onClick={prevMonth} style={{
            background: 'transparent', border: '0.5px solid #AAB0B8', borderRadius: '6px',
            padding: '3px 8px', cursor: 'pointer', color: '#22272D',
            fontFamily: 'Manrope, sans-serif', fontSize: '12px',
          }}>←</button>
          <span style={{
            fontFamily: 'Sora, sans-serif', fontSize: '12px', fontWeight: '600',
            color: '#22272D', minWidth: '110px', textAlign: 'center',
          }}>
            {monthName}
          </span>
          <button type="button" onClick={nextMonth} style={{
            background: 'transparent', border: '0.5px solid #AAB0B8', borderRadius: '6px',
            padding: '3px 8px', cursor: 'pointer', color: '#22272D',
            fontFamily: 'Manrope, sans-serif', fontSize: '12px',
          }}>→</button>
          <button type="button" onClick={goToToday} style={{
            background: '#F1FF5C', border: 'none', borderRadius: '6px',
            padding: '3px 10px', cursor: 'pointer', color: '#22272D',
            fontFamily: 'Manrope, sans-serif', fontSize: '11px', fontWeight: '600',
          }}>Today</button>
        </div>
      </div>

      {/* Day headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', marginBottom: '2px' }}>
        {DAY_HEADERS.map((d, i) => (
          <div key={i} style={{
            textAlign: 'center', fontSize: '10px', fontWeight: '600',
            color: i === 0 || i === 6 ? '#FF766B' : '#AAB0B8',
            fontFamily: 'Manrope, sans-serif', padding: '3px 0',
          }}>
            {d}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px' }}>
        {cells.map((day, i) => {
          if (!day) return <div key={`blank-${i}`} />;
          const info       = getDayInfo(day);
          const isSelected = selectedDay === day;
          const hasEvents  = !!info.holiday || info.meetings.length > 0;

          return (
            <div
              key={`day-${day}`}
              onClick={() => setSelectedDay(isSelected ? null : day)}
              style={{
                minHeight: '36px', borderRadius: '6px', padding: '3px 2px',
                cursor: 'pointer',
                background: isSelected
                  ? '#22272D'
                  : info.isToday ? 'rgba(241,255,92,0.15)' : 'transparent',
                border: info.isToday && !isSelected ? '1px solid #F1FF5C' : '1px solid transparent',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
              onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = info.isToday ? 'rgba(241,255,92,0.15)' : 'transparent'; }}
            >
              <div style={{
                textAlign: 'center', fontSize: '11px',
                fontWeight: info.isToday ? '700' : '400',
                color: isSelected
                  ? '#F1FF5C'
                  : info.isToday ? '#22272D'
                  : info.isWeekend ? '#FF766B' : '#22272D',
                fontFamily: 'Manrope, sans-serif', marginBottom: '2px',
              }}>
                {day}
              </div>
              {/* Event dots */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '1px', flexWrap: 'wrap' }}>
                {info.holiday && (
                  <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: DOT_COLORS.holiday, flexShrink: 0 }} />
                )}
                {info.meetings.map((mt, j) => (
                  <div key={j} style={{ width: '4px', height: '4px', borderRadius: '50%', background: DOT_COLORS[mt] || DOT_COLORS['Other'], flexShrink: 0 }} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Selected day detail */}
      {selectedInfo && (
        <div style={{
          marginTop: '12px', padding: '10px', borderRadius: '8px',
          background: '#F4FAFF', border: '0.5px solid #AAB0B8',
        }}>
          <div style={{
            fontSize: '12px', fontWeight: '600', color: '#22272D',
            fontFamily: 'Sora, sans-serif', marginBottom: '6px',
          }}>
            {new Date(viewYear, viewMonth, selectedDay).toLocaleDateString('en-GB', {
              weekday: 'long', day: '2-digit', month: 'long',
            })}
          </div>
          {selectedInfo.holiday && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: DOT_COLORS.holiday, flexShrink: 0 }} />
              <span style={{ fontSize: '11px', color: '#a83232', fontFamily: 'Manrope, sans-serif' }}>
                {selectedInfo.holiday}
              </span>
            </div>
          )}
          {selectedInfo.meetings.length > 0
            ? selectedInfo.meetings.map((mt, j) => {
                const s = MEETING_TYPE_STYLES[mt] || MEETING_TYPE_STYLES['Other'];
                return (
                  <div key={j} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: DOT_COLORS[mt] || DOT_COLORS['Other'], flexShrink: 0 }} />
                    <span style={{ fontSize: '11px', color: s.color, fontFamily: 'Manrope, sans-serif', fontWeight: '600' }}>
                      {mt}
                    </span>
                  </div>
                );
              })
            : !selectedInfo.holiday && (
                <div style={{ fontSize: '11px', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif' }}>
                  No meetings or events on this day
                </div>
              )
          }
        </div>
      )}

      {/* Legend */}
      <div style={{ display: 'flex', gap: '10px', marginTop: '12px', flexWrap: 'wrap' }}>
        {LEGEND_ITEMS.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: item.color }} />
            <span style={{ fontSize: '10px', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif' }}>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Home ──────────────────────────────────────────────────────────────────────

function Home({ user }) {
  const [stats,     setStats]     = useState({ total_meetings: 0, total_docs: 0 });
  const [allDocs,   setAllDocs]   = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [statsRes, docsRes, schedulesRes] = await Promise.all([
        axios.get(`${API_BASE}/api/stats`,      { withCredentials: true }),
        axios.get(`${API_BASE}/api/documents`,  { withCredentials: true }),
        axios.get(`${API_BASE}/api/schedules`,  { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setAllDocs(docsRes.data);
      setSchedules(schedulesRes.data);
    } catch {
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Calendar dots — merge document dates AND scheduled meeting dates
  const meetingDates = useMemo(() => {
    const map = {};
    allDocs.forEach(doc => {
      if (!map[doc.date]) map[doc.date] = [];
      const mt = doc.meeting_type || 'Board Meeting';
      if (!map[doc.date].includes(mt)) map[doc.date].push(mt);
    });
    schedules.forEach(s => {
      if (!map[s.date]) map[s.date] = [];
      const mt = s.meeting_type || 'Other';
      if (!map[s.date].includes(mt)) map[s.date].push(mt);
    });
    return map;
  }, [allDocs, schedules]);

  // Next meeting — based ONLY on scheduled meetings
  const upcomingMeeting = useMemo(() => {
    const todayKey = toDateKey(new Date());
    const nowTime  = new Date().toTimeString().slice(0, 5);
    const future = schedules
      .filter(s => s.date > todayKey || (s.date === todayKey && s.time >= nowTime))
      .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
    if (future.length === 0) return null;
    const s = future[0];
    return {
      dateKey: s.date,
      type: s.meeting_type || 'Other',
      time: s.time,
      location: s.location,
    };
  }, [schedules]);

  // Last meeting — most recent PAST scheduled meeting
  const lastMeetingDisplay = useMemo(() => {
    const todayKey = toDateKey(new Date());
    const nowTime  = new Date().toTimeString().slice(0, 5);
    const past = schedules
      .filter(s => s.date < todayKey || (s.date === todayKey && s.time < nowTime))
      .sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));
    if (past.length === 0) return 'N/A';
    return formatDate(past[0].date);
  }, [schedules]);

  const statCards = useMemo(() => [
    { label: 'Total meetings', value: stats.total_meetings },
    { label: 'Total documents', value: stats.total_docs },
    { label: 'Last meeting', value: lastMeetingDisplay },
    {
      label: upcomingMeeting ? `Next — ${upcomingMeeting.type}` : 'Next meeting',
      value: upcomingMeeting
        ? (() => { const d = parseDateLocal(upcomingMeeting.dateKey); return d ? formatFull(d) : upcomingMeeting.dateKey; })()
        : 'Not scheduled',
    },
  ], [stats, lastMeetingDisplay, upcomingMeeting]);

  // Permission helper
  const isSuper = user?.role === 'superadmin';
  const perms = user?.permissions || {};

  // Quick actions — each shows based on user's permission
  const quickActions = useMemo(() => {
    const actions = [];
    if (isSuper || !!perms.upload) {
      actions.push({ label: 'Upload Document', path: '/upload', bg: '#F1FF5C', color: '#22272D' });
    }
    actions.push({ label: 'View Documents', path: '/documents', bg: '#62F0FF', color: '#22272D' });
    if (isSuper || !!(perms.manage_users && (perms.manage_users.add || perms.manage_users.edit || perms.manage_users.delete))) {
      actions.push({ label: 'Manage Users', path: '/users', bg: '#BDFFC0', color: '#22272D' });
    }
    return actions;
  }, [isSuper, perms]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', flexDirection: 'column', gap: '16px' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '50%', border: '3px solid #F4FAFF', borderTop: '3px solid #F1FF5C', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ fontSize: '13px', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif' }}>Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', flexDirection: 'column', gap: '16px' }}>
        <div style={{ fontSize: '14px', color: '#FF766B', fontFamily: 'Manrope, sans-serif' }}>{error}</div>
        <button type="button" onClick={fetchData} style={{ padding: '10px 24px', borderRadius: '8px', border: 'none', background: '#F1FF5C', color: '#22272D', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'Manrope, sans-serif' }}>Retry</button>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ fontFamily: 'Sora, sans-serif', fontSize: '22px', fontWeight: '600', color: '#22272D', marginBottom: '4px' }}>
        Welcome back, {user.name} 👋
      </h1>
      <p style={{ fontSize: '13px', color: '#AAB0B8', marginBottom: '28px' }}>Here's your meeting summary</p>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
        {statCards.map((card, i) => (
          <div key={i} style={{ background: '#22272D', borderRadius: '12px', padding: '18px' }}>
            <div style={{ fontSize: '11px', color: '#AAB0B8', marginBottom: '8px' }}>{card.label}</div>
            <div style={{ fontFamily: 'Sora, sans-serif', fontSize: i > 1 ? '13px' : '26px', fontWeight: '600', color: '#F1FF5C', lineHeight: 1.3 }}>
              {card.value}
            </div>
          </div>
        ))}
      </div>

      {/* Two-column: left = upcoming + quick actions, right = calendar */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px', alignItems: 'stretch' }}>

        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Upcoming meetings */}
          <div style={{ background: '#fff', border: '0.5px solid #AAB0B8', borderRadius: '12px', padding: '20px' }}>
            <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: '15px', fontWeight: '600', color: '#22272D', marginBottom: '14px', marginTop: 0 }}>
              Upcoming meetings
            </h2>
            {upcomingMeeting ? (() => {
              const d = parseDateLocal(upcomingMeeting.dateKey);
              const s = MEETING_TYPE_STYLES[upcomingMeeting.type] || MEETING_TYPE_STYLES['Other'];
              return (
                <div style={{ padding: '12px 16px', borderRadius: '10px', background: s.bg, border: `0.5px solid ${s.color}30` }}>
                  <div style={{ fontSize: '11px', color: s.color, fontWeight: '600', marginBottom: '4px' }}>
                    {upcomingMeeting.type}
                  </div>
                  <div style={{ fontSize: '13px', color: '#22272D', fontWeight: '500', fontFamily: 'Manrope, sans-serif' }}>
                    {d ? formatFull(d) : upcomingMeeting.dateKey}
                    {upcomingMeeting.time ? ` · ${upcomingMeeting.time}` : ''}
                  </div>
                  {upcomingMeeting.location && (
                    <div style={{ fontSize: '12px', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif', marginTop: '4px' }}>
                      {upcomingMeeting.location}
                    </div>
                  )}
                </div>
              );
            })() : (
              <div style={{ fontSize: '13px', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif' }}>
                No upcoming meetings scheduled
              </div>
            )}
          </div>

          {/* Quick actions */}
          <div style={{ background: '#fff', border: '0.5px solid #AAB0B8', borderRadius: '12px', padding: '20px' }}>
            <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: '15px', fontWeight: '600', color: '#22272D', marginBottom: '14px', marginTop: 0 }}>
              Quick actions
            </h2>
            {quickActions.length === 0 ? (
              <div style={{ fontSize: '13px', color: '#AAB0B8', fontFamily: 'Manrope, sans-serif' }}>
                No actions available for your role
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {quickActions.map((action, i) => (
                  <button key={i} type="button" onClick={() => navigate(action.path)} style={{
                    padding: '10px 20px', borderRadius: '8px', border: 'none',
                    background: action.bg, color: action.color, textAlign: 'left',
                    fontSize: '13px', fontWeight: '600', fontFamily: 'Manrope, sans-serif',
                    cursor: 'pointer', transition: 'opacity 0.15s',
                  }}>
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>

        </div>

        {/* Right column — Calendar */}
        <Calendar meetingDates={meetingDates} />

      </div>
    </div>
  );
}

export default Home;